import api from "./api";
import type {
  ArchiveDeck,
  BidResult,
  ArchiveDeckDetail,
  G2bLookupResult,
  G2bSettings,
  PagePlanItem,
  ProposalFormTemplateInfo,
  ProposalPageDetail,
  ProposalPagesResponse,
  ProposalProjectDetail,
  ProposalProjectListItem,
  ProposalProjectStats,
  ProposalQaQuestion,
  ProposalReference,
  ReviewMode,
  UploadReviewResponse,
  BusinessLinkChoice,
  BusinessLinkInfo,
  BusinessLinkCandidate,
  BusinessSyncResult,
} from "@/types/proposal";

export type QaGuestData =
  | {
      scope: "project";
      expert_name: string;
      project_name: string;
      questions: {
        id: number; question: string; category: string | null;
        ai_draft: string | null; final_answer: string | null;
        opinions: { author: string; content: string; created_at: string | null }[];
      }[];
    }
  | {
      scope: "qa";
      expert_name: string; project_name: string;
      question: string; category: string | null; ai_draft: string | null;
    };

/** presigned URL 다운로드 — 같은 오리진(/storage/ 프록시)이므로 앵커 download 로 파일명 보장 */
function downloadUrl(url: string, filename?: string | null) {
  const a = document.createElement("a");
  a.href = url;
  if (filename) a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
}

// AI 검토(사전검토·취합)는 로컬 LLM 응답이 느릴 수 있어 타임아웃 연장
const AI_TIMEOUT = 180_000;

class ProposalService {
  private base = "/proposals";

  // ----- 배지 -----
  async pendingCount(): Promise<{ count: number }> {
    const r = await api.get(`${this.base}/pending-count`);
    return r.data;
  }

  // ----- 프로젝트 -----
  async listProjects(params?: { status?: string }): Promise<ProposalProjectListItem[]> {
    const r = await api.get(`${this.base}/projects`, { params });
    return r.data;
  }

  async createProject(data: {
    name: string;
    client_name?: string;
    description?: string;
    submission_deadline?: string;
    presentation_date?: string;
    g2b_bid_no?: string;
    g2b_bid_ord?: string;
    g2b_url?: string;
    rfp_file?: File | null;
    business_link?: BusinessLinkChoice | null; // 사업관리 연동 (등록 시점 연결)
  }): Promise<{ success: boolean; id: number; rfp_warning: string | null; business: BusinessLinkInfo | null }> {
    const form = new FormData();
    const bl = data.business_link;
    if (bl && bl.mode !== "none") {
      form.append("business_mode", bl.mode);
      if (bl.business_project_id) form.append("business_project_id", String(bl.business_project_id));
      if (bl.project_type) form.append("business_project_type", bl.project_type);
      if (bl.payment_method) form.append("business_payment_method", bl.payment_method);
      if (bl.project_amount != null) form.append("business_project_amount", String(bl.project_amount));
      if (bl.expected_month) form.append("business_expected_month", bl.expected_month);
    }
    form.append("name", data.name);
    if (data.client_name) form.append("client_name", data.client_name);
    if (data.description) form.append("description", data.description);
    if (data.submission_deadline) form.append("submission_deadline", data.submission_deadline);
    if (data.presentation_date) form.append("presentation_date", data.presentation_date);
    if (data.g2b_bid_no) form.append("g2b_bid_no", data.g2b_bid_no);
    if (data.g2b_bid_ord) form.append("g2b_bid_ord", data.g2b_bid_ord);
    if (data.g2b_url) form.append("g2b_url", data.g2b_url);
    if (data.rfp_file) form.append("rfp_file", data.rfp_file);
    const r = await api.post(`${this.base}/projects`, form, {
      headers: { "Content-Type": "multipart/form-data" },
      timeout: 120_000,
    });
    return r.data;
  }

  async getProject(id: number): Promise<ProposalProjectDetail> {
    const r = await api.get(`${this.base}/projects/${id}`);
    return r.data;
  }

  async updateProject(id: number, data: Record<string, unknown>): Promise<void> {
    await api.put(`${this.base}/projects/${id}`, data);
  }

  async deleteProject(id: number): Promise<void> {
    await api.delete(`${this.base}/projects/${id}`);
  }

  /** 입찰 결과 입력 — 수주/미수주를 기록해야 프로젝트가 완료로 바뀐다 (정정 시 덮어쓰기) */
  async recordBidResult(id: number, data: {
    result: "won" | "lost";
    decided_on?: string | null;
    winner_name?: string | null;
    note?: string | null;
    business_link?: BusinessLinkChoice | null; // 미연결 제안이면 결과와 함께 연결/등록
  }): Promise<{ success: boolean; status: string; bid_result: BidResult; business_sync: BusinessSyncResult | null }> {
    const bl = data.business_link;
    const r = await api.post(`${this.base}/projects/${id}/bid-result`, {
      ...data,
      business_link: bl && bl.mode !== "none" ? bl : null,
    });
    return r.data;
  }

  /** 입찰 결과 취소 — 기록을 지우고 '제출완료'로 되돌림 */
  async clearBidResult(id: number): Promise<{ success: boolean; status: string; business_sync: BusinessSyncResult | null }> {
    const r = await api.delete(`${this.base}/projects/${id}/bid-result`);
    return r.data;
  }

  // ----- 사업관리 연동 (prop38) -----
  /** 제안 등록 전 후보 조회 — 영업이 먼저 등록한 '준비중' 사업 중 같은 건 */
  async getBusinessCandidates(name: string, clientName?: string | null): Promise<BusinessLinkCandidate[]> {
    const r = await api.get(`${this.base}/projects/business-candidates`, {
      params: { name, client_name: clientName || undefined },
    });
    return r.data;
  }

  async getBusinessLink(projectId: number): Promise<{
    linked: BusinessLinkInfo | null;
    candidates: BusinessLinkCandidate[];
    amount_default: number | null;
    expected_month_default: string | null;
  }> {
    const r = await api.get(`${this.base}/projects/${projectId}/business-link`);
    return r.data;
  }

  async setBusinessLink(projectId: number, choice: BusinessLinkChoice): Promise<{ success: boolean; business: BusinessLinkInfo }> {
    const r = await api.post(`${this.base}/projects/${projectId}/business-link`, choice);
    return r.data;
  }

  async clearBusinessLink(projectId: number): Promise<void> {
    await api.delete(`${this.base}/projects/${projectId}/business-link`);
  }

  // ----- 멤버 -----
  async addMembers(projectId: number, userIds: string[], role: "manager" | "lead" | "member"): Promise<void> {
    await api.post(`${this.base}/projects/${projectId}/members`, { user_ids: userIds, role });
  }

  async updateMemberRole(projectId: number, userId: string, role: "manager" | "lead" | "member"): Promise<void> {
    await api.put(`${this.base}/projects/${projectId}/members/${userId}`, null, { params: { role } });
  }

  async removeMember(projectId: number, userId: string): Promise<void> {
    await api.delete(`${this.base}/projects/${projectId}/members/${userId}`);
  }

  // ----- 공고 자료 (RFP 문서 묶음) -----
  async uploadRfp(projectId: number, file: File, replaceRfp = false): Promise<{ extracted_chars: number; warning: string | null }> {
    const form = new FormData();
    form.append("file", file);
    if (replaceRfp) form.append("replace_rfp", "true");
    const r = await api.post(`${this.base}/projects/${projectId}/rfp`, form, {
      headers: { "Content-Type": "multipart/form-data" },
      timeout: 120_000,
    });
    return r.data;
  }

  async downloadRfpDoc(docId: number): Promise<void> {
    const r = await api.get(`${this.base}/rfp-docs/${docId}/download`);
    downloadUrl(r.data.url, r.data.file_name);
  }

  async deleteRfpDoc(docId: number): Promise<void> {
    await api.delete(`${this.base}/rfp-docs/${docId}`);
  }

  async aiRfpAnalyze(projectId: number): Promise<{ started: boolean; skipped?: boolean; message?: string }> {
    const r = await api.post(`${this.base}/projects/${projectId}/ai/rfp-analyze`);
    return r.data;
  }

  async getAiStatus(projectId: number): Promise<Record<"req_extract" | "strategy_draft" | "extra_ideas" | "auto_map" | "rfp_analyze", boolean>> {
    const r = await api.get(`${this.base}/projects/${projectId}/ai-status`);
    return r.data;
  }

  async aiStrategyDraft(projectId: number): Promise<{ started: boolean }> {
    const r = await api.post(`${this.base}/projects/${projectId}/ai/strategy-draft`);
    return r.data;
  }

  async g2bCheckNow(projectId: number): Promise<{ first: boolean; changes: import("@/types/proposal").G2bChangeEntry["changes"]; checked_at: string }> {
    const r = await api.post(`${this.base}/projects/${projectId}/g2b/check`, undefined, { timeout: 60_000 });
    return r.data;
  }

  async g2bAckChanges(projectId: number): Promise<void> {
    await api.post(`${this.base}/projects/${projectId}/g2b/changes/ack`);
  }

  async aiReqChangeImpact(projectId: number): Promise<{ started: boolean }> {
    const r = await api.post(`${this.base}/projects/${projectId}/requirements/change-impact`);
    return r.data;
  }

  async getReqChangeImpact(projectId: number): Promise<{ status: "none" | "running" | "ready"; report: import("@/types/proposal").ReqChangeImpactReport | null }> {
    const r = await api.get(`${this.base}/projects/${projectId}/requirements/change-impact`);
    return r.data;
  }

  async dismissReqChangeImpact(projectId: number): Promise<void> {
    await api.delete(`${this.base}/projects/${projectId}/requirements/change-impact`);
  }

  async getStrategyDraftStatus(projectId: number): Promise<{ status: "none" | "running" | "ready"; draft: string | null }> {
    const r = await api.get(`${this.base}/projects/${projectId}/ai/strategy-draft`);
    return r.data;
  }

  async aiExtraIdeas(projectId: number): Promise<{ started: boolean }> {
    const r = await api.post(`${this.base}/projects/${projectId}/ai/extra-ideas`);
    return r.data;
  }

  async aiPageDraft(pageId: number): Promise<{ started: boolean }> {
    const r = await api.post(`${this.base}/pages/${pageId}/ai/draft`);
    return r.data;
  }

  async aiPageGuidesBulk(projectId: number, body: { page_ids?: number[]; part_name?: string; only_missing?: boolean }): Promise<{ started: boolean; count: number; message?: string }> {
    const r = await api.post(`${this.base}/projects/${projectId}/ai/guides`, body);
    return r.data;
  }

  async downloadSkeletonPptx(projectId: number, partName?: string): Promise<void> {
    const r = await api.get(`${this.base}/projects/${projectId}/skeleton.pptx`, {
      params: partName ? { part_name: partName } : undefined, responseType: "blob", timeout: 120_000,
    });
    const cd = String(r.headers["content-disposition"] ?? "");
    const m = /filename\*=UTF-8''([^;]+)/.exec(cd);
    downloadUrl(URL.createObjectURL(r.data), m ? decodeURIComponent(m[1]) : `골격${partName ? "_" + partName : ""}.pptx`);
  }

  async aiPagePlan(projectId: number, partName?: string): Promise<{ started: boolean }> {
    const r = await api.post(`${this.base}/projects/${projectId}/ai/page-plan`, undefined, { params: partName ? { part_name: partName } : undefined });
    return r.data;
  }

  async dismissPagePlan(projectId: number): Promise<void> {
    await api.delete(`${this.base}/projects/${projectId}/ai/page-plan`);
  }

  async getPagePlanStatus(projectId: number): Promise<{ status: "none" | "running" | "ready"; items: PagePlanItem[] | null }> {
    const r = await api.get(`${this.base}/projects/${projectId}/ai/page-plan`);
    return r.data;
  }

  // ----- 나라장터(G2B) 연동 -----
  async g2bLookup(ref: string): Promise<G2bLookupResult> {
    const r = await api.get(`${this.base}/g2b/lookup`, { params: { ref }, timeout: 60_000 });
    return r.data;
  }

  async getG2bSettings(): Promise<G2bSettings> {
    const r = await api.get(`${this.base}/g2b/settings`);
    return r.data;
  }

  async saveG2bSettings(serviceKey: string): Promise<void> {
    await api.put(`${this.base}/g2b/settings`, { service_key: serviceKey });
  }

  async getAiSettings(): Promise<import("@/types/proposal").ProposalAiSettings> {
    const r = await api.get(`${this.base}/settings/ai`);
    return r.data;
  }

  async saveAiSettings(payload: { standard_model: string | null; advanced_model: string | null; light_model?: string | null; evaluator_provider_id?: string | null }): Promise<void> {
    await api.put(`${this.base}/settings/ai`, payload);
  }

  async importRfpFromG2b(projectId: number, attachments: { url: string; file_name?: string }[]): Promise<{
    imported: number; extracted_chars: number; warnings: string[];
    rfp_ready?: boolean; rfp_notice?: string | null;
  }> {
    // 서버가 나라장터에서 첨부 전체를 내려받아 저장 — 대용량 대비 타임아웃 연장
    const r = await api.post(`${this.base}/projects/${projectId}/rfp/from-g2b`, { attachments }, { timeout: 300_000 });
    return r.data;
  }

  // ----- 평가 기준 관리 -----
  async listReviewCriteria(): Promise<import("@/types/proposal").ReviewCriteriaResponse> {
    const r = await api.get(`${this.base}/review-criteria`);
    return r.data;
  }

  async createReviewCriterion(data: {
    category: string; name: string; description?: string; severity: string; weight: number;
  }): Promise<void> {
    await api.post(`${this.base}/review-criteria`, data);
  }

  async updateReviewCriterion(id: number, data: Record<string, unknown>): Promise<void> {
    await api.put(`${this.base}/review-criteria/${id}`, data);
  }

  async deleteReviewCriterion(id: number): Promise<void> {
    await api.delete(`${this.base}/review-criteria/${id}`);
  }

  async saveReviewSettings(passScore: number): Promise<void> {
    await api.put(`${this.base}/review-settings`, { pass_score: passScore });
  }

  // ----- 기여 현황 -----
  async getProjectStats(projectId: number): Promise<ProposalProjectStats> {
    const r = await api.get(`${this.base}/projects/${projectId}/stats`);
    return r.data;
  }

  async getProjectAiUsage(projectId: number): Promise<import("@/types/proposal").ProposalAiUsage> {
    const r = await api.get(`${this.base}/projects/${projectId}/ai-usage`);
    return r.data;
  }

  // ----- 요구사항 추적 (조견표) -----

  async reqAiStrategy(reqId: number): Promise<{ strategy: string | null }> {
    const r = await api.post(`${this.base}/requirements/${reqId}/ai-strategy`, undefined, { timeout: 180_000 });
    return r.data;
  }

  async listRequirements(projectId: number): Promise<import("@/types/proposal").RequirementsResponse> {
    const r = await api.get(`${this.base}/projects/${projectId}/requirements`);
    return r.data;
  }

  /** 페이지(목차) 엑셀 일괄 등록 양식 — 서버 생성(필수 컬럼 빨간 글자·안내 시트·드롭다운) */
  async downloadPagesImportTemplate(projectId: number): Promise<void> {
    const r = await api.get(`${this.base}/projects/${projectId}/pages/import-template`, {
      responseType: "blob", timeout: 60_000,
    });
    const url = URL.createObjectURL(r.data as Blob);
    downloadUrl(url, "페이지목차_양식.xlsx");
    URL.revokeObjectURL(url);
  }

  async exportRequirementsMatrix(projectId: number): Promise<void> {
    const r = await api.get(`${this.base}/projects/${projectId}/requirements/export`, {
      responseType: "blob", timeout: 60_000,
    });
    const url = URL.createObjectURL(r.data as Blob);
    downloadUrl(url, "요구사항대응표.xlsx");
    URL.revokeObjectURL(url);
  }

  async createPageFromRequirementsBulk(projectId: number, requirementIds: number[]): Promise<{
    page_id: number; title: string; mapped: number;
  }> {
    const r = await api.post(`${this.base}/projects/${projectId}/requirements/create-page-bulk`, {
      requirement_ids: requirementIds,
    });
    return r.data;
  }

  async createPageFromRequirement(projectId: number, reqId: number): Promise<{ page_id: number; title: string }> {
    const r = await api.post(`${this.base}/projects/${projectId}/requirements/${reqId}/create-page`);
    return r.data;
  }

  async requirementSuggestions(projectId: number, reqId: number): Promise<{
    suggestions: import("@/types/proposal").ProposalReference[];
  }> {
    const r = await api.get(`${this.base}/projects/${projectId}/requirements/${reqId}/suggestions`, { timeout: 60_000 });
    return r.data;
  }

  async previewArchivePage(archivePageId: number): Promise<{ page_no: number; deck_title: string; image: string }> {
    const r = await api.get(`${this.base}/archive/pages/${archivePageId}/preview`, { timeout: 120_000 });
    return r.data;
  }

  async adoptArchivePageForRequirement(projectId: number, reqId: number, archivePageId: number): Promise<{
    page_id: number; title: string; attaching_source: boolean;
  }> {
    const r = await api.post(
      `${this.base}/projects/${projectId}/requirements/${reqId}/adopt-archive-page`,
      { archive_page_id: archivePageId },
    );
    return r.data;
  }

  async extractRequirements(projectId: number, force = false): Promise<{ started: boolean; message: string }> {
    const r = await api.post(
      `${this.base}/projects/${projectId}/requirements/extract`, null,
      { params: { force }, timeout: AI_TIMEOUT },
    );
    return r.data;
  }

  async createRequirement(projectId: number, data: {
    req_code: string; category?: string; title: string; content?: string;
  }): Promise<{ id: number }> {
    const r = await api.post(`${this.base}/projects/${projectId}/requirements`, data);
    return r.data;
  }

  async updateRequirement(reqId: number, data: {
    req_code?: string; category?: string; title?: string; content?: string;
  }): Promise<void> {
    await api.put(`${this.base}/requirements/${reqId}`, data);
  }

  async deleteRequirement(reqId: number): Promise<void> {
    await api.delete(`${this.base}/requirements/${reqId}`);
  }

  async addRequirementPage(reqId: number, pageId: number): Promise<void> {
    await api.post(`${this.base}/requirements/${reqId}/pages`, { page_id: pageId });
  }

  async removeRequirementPage(reqId: number, pageId: number): Promise<void> {
    await api.delete(`${this.base}/requirements/${reqId}/pages/${pageId}`);
  }

  /** 요구사항 ↔ 페이지 제목 일치 자동 매칭 — 결정적·즉시·무료 (기존 매핑 유지, 빠진 것만 추가) */
  async matchRequirementsByTitle(projectId: number): Promise<{
    added: number;
    matches: { req_code: string; req_title: string; page_no: number | null; page_title: string; reason: string }[];
  }> {
    const r = await api.post(`${this.base}/projects/${projectId}/requirements/match-by-title`);
    return r.data;
  }

  async autoMapRequirements(projectId: number): Promise<{ started: boolean; message: string }> {
    const r = await api.post(
      `${this.base}/projects/${projectId}/requirements/auto-map`, null, { timeout: AI_TIMEOUT },
    );
    return r.data;
  }

  // ----- 양식 -----
  async listForms(): Promise<ProposalFormTemplateInfo[]> {
    const r = await api.get(`${this.base}/forms`);
    return r.data;
  }

  async uploadForm(data: { file: File; name: string; note?: string }): Promise<void> {
    const form = new FormData();
    form.append("file", data.file);
    form.append("name", data.name);
    if (data.note) form.append("note", data.note);
    await api.post(`${this.base}/forms`, form, {
      headers: { "Content-Type": "multipart/form-data" },
      timeout: 120_000,
    });
  }

  async downloadForm(formId: number): Promise<void> {
    const r = await api.get(`${this.base}/forms/${formId}/download`);
    downloadUrl(r.data.url, r.data.file_name);
  }

  async deleteForm(formId: number): Promise<void> {
    await api.delete(`${this.base}/forms/${formId}`);
  }

  // ----- 페이지 -----
  async listPages(projectId: number): Promise<ProposalPagesResponse> {
    const r = await api.get(`${this.base}/projects/${projectId}/pages`);
    return r.data;
  }

  async createPages(
    projectId: number,
    items: import("@/types/proposal").ProposalPageCreateItem[],
  ): Promise<{ created: number; mapped: number; page_ids?: number[] }> {
    const r = await api.post(`${this.base}/projects/${projectId}/pages`, { items });
    return r.data;
  }

  // ----- 파트 (정성/정량/발표) -----
  async createPart(projectId: number, data: { name: string; lead_user_id?: string; due_date?: string }): Promise<void> {
    await api.post(`${this.base}/projects/${projectId}/parts`, data);
  }

  async createDefaultParts(projectId: number): Promise<void> {
    await api.post(`${this.base}/projects/${projectId}/parts/default`);
  }

  async updatePart(partId: number, data: Record<string, unknown>): Promise<void> {
    await api.put(`${this.base}/parts/${partId}`, data);
  }

  async deletePart(partId: number, deletePages = false): Promise<{ deleted_pages: number }> {
    const r = await api.delete(`${this.base}/parts/${partId}`, { params: { delete_pages: deletePages } });
    return r.data;
  }

  async bulkDeletePages(projectId: number, pageIds: number[]): Promise<{ deleted: number }> {
    const r = await api.post(`${this.base}/projects/${projectId}/pages/bulk-delete`, { page_ids: pageIds });
    return r.data;
  }

  async aiClassifyPages(projectId: number, scope: "missing" | "all" = "missing"): Promise<{
    classified: number; total?: number; message?: string;
  }> {
    const r = await api.post(`${this.base}/projects/${projectId}/ai/classify-pages`, null, {
      params: { scope }, timeout: AI_TIMEOUT,
    });
    return r.data;
  }

  async bulkUpdatePages(projectId: number, data: {
    page_ids: number[];
    assignee_id?: string;
    due_date?: string;
    part_id?: number;
    category?: string;
    section_path?: string;
  }): Promise<{ updated: number }> {
    const r = await api.post(`${this.base}/projects/${projectId}/pages/bulk-update`, data);
    return r.data;
  }

  async autoAssignPages(projectId: number, data: { scope: "unassigned" | "all"; set_due: boolean; balance_by_capacity?: boolean }): Promise<{
    assigned: number;
    message?: string;
    distribution?: { user_id: string; name: string | null; count: number }[];
  }> {
    const r = await api.post(`${this.base}/projects/${projectId}/pages/auto-assign`, data);
    return r.data;
  }

  async pageAiGuide(pageId: number): Promise<{ guide: string }> {
    const r = await api.post(`${this.base}/pages/${pageId}/ai-guide`, null, { timeout: AI_TIMEOUT });
    return r.data;
  }

  // ----- 구조 템플릿 -----
  async listStructureTemplates(): Promise<import("@/types/proposal").StructureTemplate[]> {
    const r = await api.get(`${this.base}/structure-templates`);
    return r.data;
  }

  async saveStructureTemplate(data: { name: string; description?: string; from_project_id: number }): Promise<void> {
    await api.post(`${this.base}/structure-templates`, data);
  }

  async deleteStructureTemplate(templateId: number): Promise<void> {
    await api.delete(`${this.base}/structure-templates/${templateId}`);
  }

  async applyStructure(projectId: number, templateId: number, mode: "all" | "reusable_only" = "all"): Promise<{
    created_pages: number; created_parts: number; skipped_rfp: number; attaching_sources: number;
  }> {
    const r = await api.post(`${this.base}/projects/${projectId}/apply-structure`, { template_id: templateId, mode });
    return r.data;
  }

  // ----- 제출 조립 -----
  async assembleSubmission(projectId: number, data: {
    part_id?: number | null; scope: "approved" | "uploaded"; format: "pdf" | "zip"; normalize?: boolean;
  }): Promise<{ message: string }> {
    const r = await api.post(`${this.base}/projects/${projectId}/assemble`, data);
    return r.data;
  }

  async listAssembleExports(projectId: number): Promise<{ exports: import("@/types/proposal").AssembleExport[] }> {
    const r = await api.get(`${this.base}/projects/${projectId}/assemble/exports`);
    return r.data;
  }

  async downloadAssembleExport(projectId: number, index: number): Promise<void> {
    const r = await api.get(`${this.base}/projects/${projectId}/assemble/download/${index}`);
    downloadUrl(r.data.url, r.data.file_name);
  }

  // ----- 종합 평가 (숲·산) -----
  async runHolisticExternal(projectId: number, file: File, docType: string, cross = false) {
    const form = new FormData();
    form.append("file", file);
    form.append("doc_type", docType);
    if (cross) form.append("cross", "true");
    const r = await api.post(`${this.base}/projects/${projectId}/ai/holistic-review/external`, form, {
      headers: { "Content-Type": "multipart/form-data" }, timeout: 280_000,
    });
    return r.data as { success: boolean; review: import("@/types/proposal").HolisticReview };
  }

  async runHolisticReview(projectId: number, data: {
    scope_type: "project" | "part" | "category" | "reqcat";
    part_id?: number;
    category?: string; cross?: boolean }): Promise<{ review: import("@/types/proposal").HolisticReview }> {
    const r = await api.post(`${this.base}/projects/${projectId}/ai/holistic-review`, data, { timeout: AI_TIMEOUT });
    return r.data;
  }

  async listHolisticReviews(projectId: number): Promise<import("@/types/proposal").HolisticReview[]> {
    const r = await api.get(`${this.base}/projects/${projectId}/holistic-reviews`);
    return r.data;
  }

  async updatePage(pageId: number, data: Record<string, unknown>): Promise<void> {
    await api.put(`${this.base}/pages/${pageId}`, data);
  }

  async confirmSubmitVersion(versionId: number): Promise<{ page_status: string }> {
    const r = await api.post(`${this.base}/versions/${versionId}/confirm-submit`);
    return r.data;
  }

  async previewPage(pageId: number): Promise<{ version_no: number; file_name: string; images: string[] }> {
    const r = await api.get(`${this.base}/pages/${pageId}/preview`, { timeout: 120_000 });
    return r.data;
  }

  async diffVersion(versionId: number): Promise<import("@/types/proposal").VersionDiffResponse> {
    const r = await api.get(`${this.base}/versions/${versionId}/diff`);
    return r.data;
  }

  async getProjectDashboard(projectId: number): Promise<import("@/types/proposal").ProposalDashboard> {
    const r = await api.get(`${this.base}/projects/${projectId}/dashboard`);
    return r.data;
  }

  async deletePage(pageId: number): Promise<void> {
    await api.delete(`${this.base}/pages/${pageId}`);
  }

  async getPageDetail(pageId: number): Promise<ProposalPageDetail> {
    const r = await api.get(`${this.base}/pages/${pageId}`);
    return r.data;
  }

  async uploadPageVersion(pageId: number, file: File, mode: ReviewMode): Promise<UploadReviewResponse> {
    const form = new FormData();
    form.append("file", file);
    form.append("mode", mode);
    const r = await api.post(`${this.base}/pages/${pageId}/versions`, form, {
      headers: { "Content-Type": "multipart/form-data" },
      timeout: AI_TIMEOUT,
    });
    return r.data;
  }

  async getPageReferences(pageId: number): Promise<{ references: ProposalReference[] }> {
    const r = await api.get(`${this.base}/pages/${pageId}/references`, { timeout: 60_000 });
    return r.data;
  }

  async confirmPage(pageId: number): Promise<void> {
    await api.post(`${this.base}/pages/${pageId}/confirm`);
  }

  async returnPage(pageId: number, feedback: string): Promise<void> {
    await api.post(`${this.base}/pages/${pageId}/return`, { feedback });
  }

  async downloadVersion(versionId: number): Promise<void> {
    const r = await api.get(`${this.base}/versions/${versionId}/download`);
    downloadUrl(r.data.url, r.data.file_name);
  }

  // ----- 질의 대응 -----
  async qaExpectedGenerate(projectId: number): Promise<{ started: boolean }> {
    const r = await api.post(`${this.base}/projects/${projectId}/qa/expected`);
    return r.data;
  }
  async qaExpectedGenerateExternal(projectId: number, file: File, docType: string): Promise<{ started: boolean; pages: number }> {
    const form = new FormData();
    form.append("file", file);
    form.append("doc_type", docType);
    const r = await api.post(`${this.base}/projects/${projectId}/qa/expected/external`, form, { headers: { "Content-Type": "multipart/form-data" }, timeout: 120_000 });
    return r.data;
  }
  async qaExpectedStatus(projectId: number): Promise<{ status: "none" | "running" | "ready"; result: { questions: import("@/types/proposal").ExpectedQuestion[]; model?: string } | null }> {
    const r = await api.get(`${this.base}/projects/${projectId}/qa/expected`);
    return r.data;
  }
  async qaExpectedRegister(projectId: number, items: import("@/types/proposal").ExpectedQuestion[], dismiss = false): Promise<{ created: number }> {
    const r = await api.post(`${this.base}/projects/${projectId}/qa/expected/register`, { items, dismiss });
    return r.data;
  }

  async listQa(projectId: number): Promise<ProposalQaQuestion[]> {
    const r = await api.get(`${this.base}/projects/${projectId}/qa`);
    return r.data;
  }

  async createQa(projectId: number, data: { question: string; category?: string; consolidator_id?: string }): Promise<void> {
    await api.post(`${this.base}/projects/${projectId}/qa`, data);
  }

  async updateQa(qaId: number, data: Record<string, unknown>): Promise<void> {
    await api.put(`${this.base}/qa/${qaId}`, data);
  }

  async deleteQa(qaId: number): Promise<void> {
    await api.delete(`${this.base}/qa/${qaId}`);
  }

  async addOpinion(qaId: number, content: string): Promise<void> {
    await api.post(`${this.base}/qa/${qaId}/opinions`, { content });
  }

  async deleteOpinion(opinionId: number): Promise<void> {
    await api.delete(`${this.base}/qa/opinions/${opinionId}`);
  }

  async qaAiDraft(qaId: number): Promise<{ draft: string }> {
    const r = await api.post(`${this.base}/qa/${qaId}/ai-draft`, null, { timeout: AI_TIMEOUT });
    return r.data;
  }

  async qaProjectExternalInvite(projectId: number, data: {
    name: string; include_draft: boolean; include_opinions: boolean; expires_hours: number;
  }): Promise<{ path: string; expires_hours: number }> {
    const r = await api.post(`${this.base}/projects/${projectId}/qa/external-invite`, data);
    return r.data;
  }

  async qaGuestView(token: string): Promise<QaGuestData> {
    const r = await api.get(`${this.base}/qa-guest/${token}`);
    return r.data;
  }

  async qaGuestOpinion(token: string, content: string, qaId?: number): Promise<void> {
    await api.post(`${this.base}/qa-guest/${token}/opinion`, { content, qa_id: qaId ?? null });
  }

  async qaAiConsolidate(qaId: number, includeDraft = true): Promise<{ draft: string }> {
    const r = await api.post(`${this.base}/qa/${qaId}/ai-consolidate`, null, {
      params: { include_draft: includeDraft }, timeout: AI_TIMEOUT,
    });
    return r.data;
  }

  // ----- 아카이브 -----
  async listArchiveDecks(): Promise<ArchiveDeck[]> {
    const r = await api.get(`${this.base}/archive/decks`);
    return r.data;
  }

  async uploadArchiveDeck(data: {
    files: File[];
    title: string;
    client_name?: string;
    business_year?: number;
    result?: string;
    note?: string;
    g2b_ref?: string;
  }): Promise<{
    count: number;
    decks: { id: number; file_name: string }[];
    id: number | null;
    processing?: boolean;
    g2b_linked?: boolean;
    warning: string | null;
  }> {
    const form = new FormData();
    for (const f of data.files) form.append("files", f);
    form.append("title", data.title);
    if (data.client_name) form.append("client_name", data.client_name);
    if (data.business_year) form.append("business_year", String(data.business_year));
    if (data.result) form.append("result", data.result);
    if (data.note) form.append("note", data.note);
    if (data.g2b_ref) form.append("g2b_ref", data.g2b_ref);
    // 임베딩 생성(CPU)·공고 RFP 다운로드 포함 — 대형 덱은 오래 걸릴 수 있음
    const r = await api.post(`${this.base}/archive/decks`, form, {
      headers: { "Content-Type": "multipart/form-data" },
      timeout: 300_000,
    });
    return r.data;
  }

  async scoreArchiveDeck(deckId: number): Promise<{ success: boolean; queued: boolean }> {
    const r = await api.post(`${this.base}/archive/decks/${deckId}/ai-score`);
    return r.data;
  }

  async archiveToTemplate(deckId: number, name: string): Promise<{ id: number; item_count: number }> {
    // LLM 목차 생성 포함 — 수십 초 소요 가능
    const r = await api.post(
      `${this.base}/archive/decks/${deckId}/to-structure-template`, { name }, { timeout: AI_TIMEOUT },
    );
    return r.data;
  }

  async getArchiveDeck(deckId: number): Promise<ArchiveDeckDetail> {
    const r = await api.get(`${this.base}/archive/decks/${deckId}`);
    return r.data;
  }

  async updateArchiveDeck(deckId: number, data: Record<string, unknown>): Promise<void> {
    await api.put(`${this.base}/archive/decks/${deckId}`, data);
  }

  async previewVersion(versionId: number): Promise<{ version_no: number; images: string[] }> {
    const r = await api.get(`${this.base}/versions/${versionId}/preview`, { timeout: 60000 });
    return r.data;
  }

  async downloadArchiveDeck(deckId: number): Promise<void> {
    const r = await api.get(`${this.base}/archive/decks/${deckId}/download`);
    downloadUrl(r.data.url, r.data.file_name);
  }

  async deleteArchiveDeck(deckId: number): Promise<void> {
    await api.delete(`${this.base}/archive/decks/${deckId}`);
  }

  async searchArchive(q: string, limit = 10): Promise<{ results: ProposalReference[] }> {
    const r = await api.get(`${this.base}/archive/search`, { params: { q, limit }, timeout: 60_000 });
    return r.data;
  }
}

const proposalService = new ProposalService();
export default proposalService;
